import { useState, useCallback, useMemo } from "react";
import toast from "components/partials/toast/toast";
import { useCurrentClient } from "hooks/api/use-clients";
import { useFeatureFlag } from "contexts/feature-flag-context";
import { useAddTouchpointToBatch } from "hooks/api/use-batches";
import { useLatestBatch } from "hooks/api/use-latest-batch";
import { useTouchpointVersion } from "hooks/api/use-touchpoint-versions";
import { isTouchpointInReview } from "utilities/touchpoint";
import AddToBatchModal from "containers/admin/clients/client/approval-queue/components/add-to-batch-modal/add-to-batch-modal";
import SelectedTouchpointsModal from "containers/admin/clients/campaign/campaign-creative-page/components/selected-touchpoints-modal/selected-touchpoints-modal";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";

interface UseAddTouchpointToBatchModalProps {
  onSuccess?: () => void;
  onRemoveSelectedItem?: (id: string) => void;
}

export const useAddTouchpointToBatchModal = ({
  onSuccess,
  onRemoveSelectedItem,
}: UseAddTouchpointToBatchModalProps = {}) => {
  const [itemsToAdd, setItemsToAdd] = useState<TouchpointAttributes[]>([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [batchId, setBatchId] = useState("");
  const [batchName, setBatchName] = useState("");
  const isAddBatchModalOpen = itemsToAdd.length > 0;

  const currentClient = useCurrentClient();
  const isBatchManagementEnabled = useFeatureFlag("batch-management");
  const addTouchpointToBatchMutation = useAddTouchpointToBatch({ clientId: currentClient.id });
  const isSubmitting = addTouchpointToBatchMutation.isPending;

  const filteredItems = useMemo(() => itemsToAdd.filter(isTouchpointInReview), [itemsToAdd]);
  const isSomeSkipped = itemsToAdd.length !== filteredItems.length;

  const isSingleItemModal = !isMultiSelectMode && filteredItems.length > 0;
  const singleTouchpoint = isSingleItemModal ? filteredItems[0] : undefined;
  const singleTouchpointVersionId = singleTouchpoint?.latestVersion?.id;

  const { data: touchpointVersion, isLoading: isTouchpointVersionLoading } = useTouchpointVersion(
    {
      clientId: currentClient.id,
      campaignId: singleTouchpoint?.campaignId ?? "",
      touchpointId: singleTouchpoint?.id ?? "",
      id: singleTouchpointVersionId ?? "",
    },
    {
      enabled:
        isAddBatchModalOpen &&
        isSingleItemModal &&
        Boolean(singleTouchpoint?.campaignId && singleTouchpoint?.id && singleTouchpointVersionId),
    },
  );

  const versionBatchId = touchpointVersion?.touchpointBatchId;
  const isVersionBatchResolved = !isTouchpointVersionLoading;

  const {
    data: latestBatch,
    isLoading: isLatestBatchLoading,
    isFetching: isLatestBatchFetching,
  } = useLatestBatch({
    clientId: currentClient.id,
    campaignId: singleTouchpoint?.campaignId,
    touchpointId: singleTouchpoint?.id,
    enabled:
      isAddBatchModalOpen &&
      isBatchManagementEnabled &&
      isSingleItemModal &&
      isVersionBatchResolved &&
      !versionBatchId &&
      Boolean(singleTouchpoint?.campaignId && singleTouchpoint?.id),
  });

  const isLatestBatchLoadingState =
    isAddBatchModalOpen &&
    isSingleItemModal &&
    isVersionBatchResolved &&
    !versionBatchId &&
    (isLatestBatchLoading || isLatestBatchFetching);
  const isBatchPrefillLoading =
    isSingleItemModal && (!isVersionBatchResolved || isLatestBatchLoadingState);
  const latestBatchId =
    isAddBatchModalOpen &&
    isSingleItemModal &&
    isVersionBatchResolved &&
    !versionBatchId &&
    !isLatestBatchLoadingState
      ? (latestBatch?.id ?? undefined)
      : undefined;

  const openAddBatchModal = useCallback((items: TouchpointAttributes | TouchpointAttributes[]) => {
    const itemsArray = Array.isArray(items) ? items : [items];
    setIsMultiSelectMode(itemsArray.length > 1);
    setBatchId("");
    setBatchName("");
    setItemsToAdd(itemsArray);
  }, []);

  const handleBatchSelection = useCallback((id: string, name?: string) => {
    setBatchId(id);
    setBatchName(name ?? "");
  }, []);

  const closeAddBatchModal = useCallback(() => {
    if (isSubmitting) return;
    setItemsToAdd([]);
    setIsMultiSelectMode(false);
    setBatchId("");
    setBatchName("");
  }, [isSubmitting]);

  const handleSingleAddToBatch = useCallback(
    async (values: { batchId: string }, selectedBatchName?: string) => {
      const item = filteredItems[0];
      if (!item || !values.batchId) return;

      await addTouchpointToBatchMutation.mutateAsync({
        batchId: values.batchId,
        attributes: {
          touchpointVersionId: item.latestVersion?.id || "",
        },
      });

      const trimmedBatchName = (selectedBatchName ?? "").trim();
      const isChangingBatch = !!versionBatchId;

      toast.success({
        data: {
          title: isChangingBatch
            ? trimmedBatchName
              ? `Changed batch to '${trimmedBatchName}'`
              : "Changed batch"
            : trimmedBatchName
              ? `Added creative to '${trimmedBatchName}'`
              : "Added creative to batch",
        },
      });

      onSuccess?.();
      closeAddBatchModal();
    },
    [
      filteredItems,
      versionBatchId,
      addTouchpointToBatchMutation,
      onSuccess,
      closeAddBatchModal,
    ],
  );

  const handleMultiAddToBatch = useCallback(async () => {
    if (filteredItems.length === 0 || !batchId) return;

    await Promise.all(
      filteredItems.map((item) =>
        addTouchpointToBatchMutation.mutateAsync({
          batchId,
          attributes: {
            touchpointVersionId: item.latestVersion?.id || "",
          },
        }),
      ),
    );

    const trimmedBatchName = batchName.trim();

    toast.success({
      data: {
        title: trimmedBatchName
          ? `Added creatives to '${trimmedBatchName}'`
          : "Added creatives to batch",
      },
    });

    onSuccess?.();
    closeAddBatchModal();
  }, [
    filteredItems,
    batchId,
    batchName,
    addTouchpointToBatchMutation,
    onSuccess,
    closeAddBatchModal,
  ]);

  const handleRemoveFromList = useCallback(
    (id: string) => {
      setItemsToAdd((prev) => prev.filter((item) => item.id !== id));
      onRemoveSelectedItem?.(id);
    },
    [onRemoveSelectedItem],
  );

  const addBatchModalMarkup = useMemo(() => {
    if (!isAddBatchModalOpen) return null;

    if (isSingleItemModal) {
      const isChangingBatch = !!versionBatchId;

      return (
        <AddToBatchModal
          onCloseCallback={closeAddBatchModal}
          onConfirmCallback={handleSingleAddToBatch}
          isSubmitting={isSubmitting}
          versionBatchId={versionBatchId}
          latestBatchId={latestBatchId}
          isLatestBatchLoading={isBatchPrefillLoading}
          title={isChangingBatch ? "Change batch" : "Add to batch"}
          submitButtonLabel={isChangingBatch ? "Change batch" : "Add to batch"}
          submittingLabel={isChangingBatch ? "Changing batch..." : "Adding to batch..."}
        />
      );
    }

    return (
      <SelectedTouchpointsModal
        onCloseCallback={closeAddBatchModal}
        onConfirmCallback={handleMultiAddToBatch}
        filteredByStatusList={filteredItems}
        isSubmitting={isSubmitting}
        setBatchId={handleBatchSelection}
        isSomeTouchpointsSkipped={isSomeSkipped}
        onRemoveSelectedItem={handleRemoveFromList}
      />
    );
  }, [
    isAddBatchModalOpen,
    isSingleItemModal,
    filteredItems,
    versionBatchId,
    latestBatchId,
    isBatchPrefillLoading,
    isSubmitting,
    isSomeSkipped,
    closeAddBatchModal,
    handleSingleAddToBatch,
    handleMultiAddToBatch,
    handleRemoveFromList,
    handleBatchSelection,
  ]);

  return {
    openAddBatchModal,
    addBatchModalMarkup,
  };
};
